import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Fade,
  Grid,
  Modal,
  TextField,
  Typography,
} from "@mui/material";

const BLUE = "#1565C0";
const BLUE_DARK = "#0D47A1";
const BORDER = "#e2e8f0";
const TEXT = "#0f172a";
const MUTED = "#64748b";

const modalBoxSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "94%", sm: "88%", md: 980 },
  maxHeight: "90vh",
  bgcolor: "#fff",
  borderRadius: 3,
  boxShadow: "none",
  border: `1px solid ${BORDER}`,
  overflow: "hidden",
};

const AdjustmentForm = ({ open, onClose, onSaved, showMessage }) => {
  const [saving, setSaving] = useState(false);
  const [cuentas, setCuentas] = useState([]);
  const [loadingCuentas, setLoadingCuentas] = useState(false);
  const [cuentaSearch, setCuentaSearch] = useState("");

  const [form, setForm] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    descripcion: "",
    lineas: [
      { cuenta: null, cuenta_id: "", descripcion: "", debe: "", haber: "" },
      { cuenta: null, cuenta_id: "", descripcion: "", debe: "", haber: "" },
    ],
  });

  const money = (value) => {
    const n = Number(value || 0);
    return n.toLocaleString("es-EC", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return "";
  };

  const totalDebe = useMemo(() => {
    return form.lineas.reduce((acc, item) => acc + Number(item.debe || 0), 0);
  }, [form.lineas]);

  const totalHaber = useMemo(() => {
    return form.lineas.reduce((acc, item) => acc + Number(item.haber || 0), 0);
  }, [form.lineas]);

  const cuadrado = useMemo(() => {
    return totalDebe > 0 && totalDebe.toFixed(2) === totalHaber.toFixed(2);
  }, [totalDebe, totalHaber]);

  const cargarCuentas = async (search = "") => {
    setLoadingCuentas(true);

    try {
      const params = new URLSearchParams();

      if (search) {
        params.set("search", search);
      }

      const res = await fetch(`/cuentas/libro-diario/cuentas-ajuste/?${params.toString()}`, {
        credentials: "include",
      });

      const data = await res.json();

      if (!data.ok) {
        throw new Error(data.error || "No se pudieron cargar las cuentas.");
      }

      setCuentas(data.cuentas || []);
    } catch (error) {
      showMessage(error.message || "No se pudieron cargar las cuentas.", "error");
    } finally {
      setLoadingCuentas(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    setForm({
      fecha: new Date().toISOString().slice(0, 10),
      descripcion: "",
      lineas: [
        { cuenta: null, cuenta_id: "", descripcion: "", debe: "", haber: "" },
        { cuenta: null, cuenta_id: "", descripcion: "", debe: "", haber: "" },
      ],
    });

    setCuentaSearch("");
    cargarCuentas("");
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => {
      cargarCuentas(cuentaSearch);
    }, 350);

    return () => clearTimeout(timer);
  }, [cuentaSearch, open]);

  const updateForm = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateLinea = (index, field, value) => {
    setForm((prev) => {
      const lineas = [...prev.lineas];

      lineas[index] = {
        ...lineas[index],
        [field]: value,
      };

      if (field === "debe" && value !== "") {
        lineas[index].haber = "";
      }

      if (field === "haber" && value !== "") {
        lineas[index].debe = "";
      }

      return {
        ...prev,
        lineas,
      };
    });
  };

  const updateCuenta = (index, cuenta) => {
    setForm((prev) => {
      const lineas = [...prev.lineas];

      lineas[index] = {
        ...lineas[index],
        cuenta,
        cuenta_id: cuenta ? cuenta.id : "",
      };

      return {
        ...prev,
        lineas,
      };
    });
  };

  const agregarLinea = () => {
    setForm((prev) => ({
      ...prev,
      lineas: [
        ...prev.lineas,
        { cuenta: null, cuenta_id: "", descripcion: "", debe: "", haber: "" },
      ],
    }));
  };

  const quitarLinea = (index) => {
    if (form.lineas.length <= 2) {
      showMessage("El ajuste debe tener al menos dos líneas.", "warning");
      return;
    }

    setForm((prev) => ({
      ...prev,
      lineas: prev.lineas.filter((_, i) => i !== index),
    }));
  };

  const guardar = async () => {
    if (!form.descripcion.trim()) {
      showMessage("Ingrese una descripción para el ajuste.", "warning");
      return;
    }

    if (!cuadrado) {
      showMessage("El ajuste debe estar cuadrado.", "warning");
      return;
    }

    const lineas = form.lineas.map((item) => ({
      cuenta_id: item.cuenta_id,
      descripcion: item.descripcion,
      debe: item.debe || "0",
      haber: item.haber || "0",
    }));

    if (lineas.some((item) => !item.cuenta_id)) {
      showMessage("Seleccione una cuenta en cada línea.", "warning");
      return;
    }

    if (lineas.some((item) => Number(item.debe || 0) <= 0 && Number(item.haber || 0) <= 0)) {
      showMessage("Cada línea debe tener un valor en debe o haber.", "warning");
      return;
    }

    setSaving(true);

    try {
      const res = await fetch("/cuentas/libro-diario/ajuste/crear/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCookie("csrftoken"),
        },
        body: JSON.stringify({
          fecha: form.fecha,
          descripcion: form.descripcion,
          lineas,
        }),
      });

      const data = await res.json();

      if (!data.ok) {
        const msg = Array.isArray(data.error)
          ? data.error.join(" ")
          : typeof data.error === "object"
            ? JSON.stringify(data.error)
            : data.error;

        throw new Error(msg || "No se pudo guardar el ajuste.");
      }

      showMessage("Ajuste contable registrado correctamente.");
      onSaved();
    } catch (error) {
      showMessage(error.message || "No se pudo guardar el ajuste.", "error");
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    if (saving) return;
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 250 }}
    >
      <Fade in={open}>
        <Box sx={modalBoxSx}>
          <Box
            sx={{
              px: 2.2,
              py: 1.6,
              borderBottom: `1px solid ${BORDER}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: 18 }}>
                Nuevo ajuste contable
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: 13, mt: 0.3 }}>
                Registra un asiento manual cuadrado con debe y haber
              </Typography>
            </Box>

            <Button
              onClick={close}
              variant="outlined"
              size="small"
              disabled={saving}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderColor: BORDER,
                color: TEXT,
              }}
            >
              Cerrar
            </Button>
          </Box>

          <Box
            sx={{
              p: 2.5,
              overflowY: "auto",
              maxHeight: "calc(90vh - 132px)",
            }}
          >
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Fecha"
                  type="date"
                  value={form.fecha}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => updateForm("fecha", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={9}>
                <TextField
                  fullWidth
                  size="small"
                  label="Descripción del ajuste"
                  value={form.descripcion}
                  onChange={(e) => updateForm("descripcion", e.target.value)}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "#ffffff",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "1.55fr 1fr 120px 120px 90px",
                  },
                  gap: 1,
                  bgcolor: "#f8fafc",
                  px: 1.5,
                  py: 1,
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: 13 }}>
                  Cuenta
                </Typography>
                <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: 13 }}>
                  Detalle
                </Typography>
                <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: 13, textAlign: "right" }}>
                  Debe
                </Typography>
                <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: 13, textAlign: "right" }}>
                  Haber
                </Typography>
                <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: 13, textAlign: "center" }}>
                  Quitar
                </Typography>
              </Box>

              {form.lineas.map((linea, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "1.55fr 1fr 120px 120px 90px",
                    },
                    gap: 1,
                    px: 1.5,
                    py: 1.2,
                    borderBottom: index === form.lineas.length - 1 ? "none" : `1px solid ${BORDER}`,
                    alignItems: "center",
                  }}
                >
                  <Autocomplete
                    size="small"
                    options={cuentas}
                    loading={loadingCuentas}
                    value={linea.cuenta}
                    onChange={(_, value) => updateCuenta(index, value)}
                    onInputChange={(_, value) => setCuentaSearch(value)}
                    getOptionLabel={(option) => (option ? `${option.codigo} ${option.nombre}` : "")}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cuenta contable"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCuentas ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label="Detalle"
                    value={linea.descripcion}
                    onChange={(e) => updateLinea(index, "descripcion", e.target.value)}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label="Debe"
                    type="number"
                    value={linea.debe}
                    inputProps={{ min: 0, step: "0.01" }}
                    onChange={(e) => updateLinea(index, "debe", e.target.value)}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label="Haber"
                    type="number"
                    value={linea.haber}
                    inputProps={{ min: 0, step: "0.01" }}
                    onChange={(e) => updateLinea(index, "haber", e.target.value)}
                  />

                  <Button
                    onClick={() => quitarLinea(index)}
                    sx={{
                      color: "#b91c1c",
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    Quitar
                  </Button>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                mt: 1.8,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Button
                onClick={agregarLinea}
                variant="outlined"
                sx={{
                  borderColor: BLUE,
                  color: BLUE,
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Agregar línea
              </Button>

              <Box sx={{ display: "flex", gap: 2.2, alignItems: "center", flexWrap: "wrap" }}>
                <Typography sx={{ fontWeight: 700, color: TEXT }}>
                  Debe: ${money(totalDebe)}
                </Typography>

                <Typography sx={{ fontWeight: 700, color: TEXT }}>
                  Haber: ${money(totalHaber)}
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 700,
                    color: cuadrado ? "#166534" : "#b91c1c",
                  }}
                >
                  {cuadrado ? "Cuadrado" : "No cuadrado"}
                </Typography>
              </Box>
            </Box>

            <Typography sx={{ mt: 1.5, fontSize: 12, color: MUTED, fontWeight: 600 }}>
              El ajuste debe tener el mismo total en debe y haber.
            </Typography>
          </Box>

          <Box
            sx={{
              px: 2.5,
              py: 1.8,
              borderTop: `1px solid ${BORDER}`,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
            }}
          >
            <Button
              onClick={close}
              disabled={saving}
              variant="outlined"
              sx={{
                color: TEXT,
                borderColor: BORDER,
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Cancelar
            </Button>

            <Button
              onClick={guardar}
              disabled={saving}
              variant="contained"
              sx={{
                bgcolor: BLUE,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: BLUE_DARK,
                  boxShadow: "none",
                },
              }}
            >
              {saving ? "Guardando..." : "Guardar ajuste"}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default AdjustmentForm;